import * as React from 'react';

import { Badge, Button, Row, Tab } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideArchive, LucideRotateCcw } from 'lucide-react';

import { RoleActionConfirmationDialog } from '../_shared/RoleActionConfirmationDialog';

import { useExternalRoleQuery } from './_graphql/useExternalRoleQuery';
import { useExternalRoleUsersQuery } from './_graphql/useExternalRoleUsersQuery';
import { useCustomerPortalRoleActionDialog } from './_hooks/useCustomerPortalRoleActionDialog';
import { CustomerPortalAddUsersToRoleDialog } from './CustomerPortalAddUsersToRoleDialog';
import { CustomerPortalRolePermissionsTab } from './CustomerPortalRolePermissionsTab';
import { CustomerPortalRoleUsersTab } from './CustomerPortalRoleUsersTab';

import { SettingsSubmodule } from '~/modules/settings/_components/_layouts/SettingsSubmodule';
import { formatMonthYear } from '~/modules/settings/_components/_shared/utils';
import {
  $currentRole,
  $initRolePermissions,
  $roleDetailsTab,
  type RoleDetailsTab,
} from '~/modules/settings/_state/roleForm.state';
import {
  settingsRouteParamAtom,
  useSettingsLocation,
} from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

const $cpRoleId = settingsRouteParamAtom(
  [
    '/customer-portal/roles/:roleId',
    '/customer-portal/roles/:roleId/permissions',
    '/customer-portal/roles/:roleId/users',
  ],
  'roleId',
);

export const CustomerPortalRoleDetailsView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const roleId = useAtomValue($cpRoleId);
  const [currentTab, setCurrentTab] = useAtom($roleDetailsTab);
  const currentRole = useAtomValue($currentRole);
  const setCurrentRole = useSetAtom($currentRole);
  const initPermissions = useSetAtom($initRolePermissions);

  const { data } = useExternalRoleQuery({ roleId: roleId ?? '' });

  const { data: usersData } = useExternalRoleUsersQuery({
    roleId: roleId ?? '',
    limit: 1000,
    offset: 0,
  });

  const userCount = usersData?.total ?? 0;

  React.useEffect(() => {
    if (data?.role) {
      setCurrentRole(data.role);
    }
  }, [data, setCurrentRole]);

  React.useEffect(() => {
    if (currentRole) {
      initPermissions();
    }
  }, [currentRole, initPermissions]);

  const roleActionDialog = useCustomerPortalRoleActionDialog({
    onSuccess: (actionType) => {
      if (actionType === 'archive') {
        setLocation('/customer-portal/roles');
      }
    },
  });

  const handleBack = () => {
    setLocation('/customer-portal/roles');
  };

  const handleArchiveClick = () => {
    if (!currentRole) return;
    roleActionDialog.openForRole(currentRole);
  };

  const handleTabChange = (tab: RoleDetailsTab) => {
    setCurrentTab(tab);
  };

  const isArchived = currentRole?.status === 'archived';

  const subtitle = currentRole
    ? `Last updated on ${formatMonthYear(currentRole.updatedAt)}${currentRole.updatedBy ? ` by ${currentRole.updatedBy}` : ''}`
    : 'Loading...';

  return (
    <>
      <SettingsSubmodule
        className="@CustomerPortalRoleDetailsView h-full"
        contentClassName="p-0"
        title={currentRole?.name ?? 'Loading...'}
        subtitle={subtitle}
        onBackPressed={handleBack}
        tools={
          <Button
            variant="outline"
            icon={
              isArchived ? (
                <LucideRotateCcw className="size-4" />
              ) : (
                <LucideArchive className="size-4" />
              )
            }
            onPress={handleArchiveClick}
            isDisabled={!currentRole || roleActionDialog.isPending}
          >
            {isArchived
              ? t.customerPortal.roles.details.actions.restore
              : t.customerPortal.roles.details.actions.archive}
          </Button>
        }
        tabs={
          <>
            <Tab
              selected={currentTab === 'permissions'}
              onClick={() => handleTabChange('permissions')}
            >
              {t.customerPortal.roles.details.tabs.permissions}
            </Tab>
            <Tab
              selected={currentTab === 'users'}
              onClick={() => handleTabChange('users')}
            >
              <Row className="items-center gap-1.5">
                {t.customerPortal.roles.details.tabs.users}
                {userCount > 0 && (
                  <Badge color="neutral" className="text-xs">
                    {userCount}
                  </Badge>
                )}
              </Row>
            </Tab>
          </>
        }
      >
        {currentTab === 'permissions' && <CustomerPortalRolePermissionsTab />}
        {currentTab === 'users' && <CustomerPortalRoleUsersTab />}
      </SettingsSubmodule>
      <CustomerPortalAddUsersToRoleDialog />

      <RoleActionConfirmationDialog
        dialogState={roleActionDialog.dialogState}
        dialogs={t.customerPortal.roles.dialogs}
        isPending={roleActionDialog.isPending}
        onConfirm={roleActionDialog.confirm}
        onCancel={roleActionDialog.cancel}
        onOpenChange={roleActionDialog.handleOpenChange}
        className="@CustomerPortalRoleDetailsViewRoleActionDialog"
      />
    </>
  );
};
